import {DI_TOKENS} from '@/di/di.tokens';
import {GameStatusMocks} from '@/services/shared/GameStatusService/GameStatusService.mocks';
import {improvementsList} from '@/services/shared/ImprovementsService/ImprovementsService.mock';
import {ClickerState, GameOperation, SpendEnergyOperation} from '@/stores/ClickerStateStore/ClickerStateStore.types';
import {GameStatusType} from '@/stores/GameStatusStore/GameStatusStore.constants';

import {injected} from 'brandi';

import {ProjectDate} from '@/services/ProjectDate/ProjectDate';
import {GameOperationsService} from '@/services/shared/GameOperationsService/GameOperationsService';
import {DateUtils} from '@/utils/DateUtils';

export class ClickerStateMocks {
  public state: ClickerState;

  constructor(private readonly projectDate: ProjectDate, private readonly gameStatusMocks: GameStatusMocks) {
    this.state =
      this.gameStatusMocks.getStatus() === GameStatusType.Mocks ? MOCKS_STATUS_CLICKER_STATE : CLICKER_STATE_MOCKS;
  }

  public applyOperations(operations: GameOperation[]): ClickerState {
    operations.forEach((o) => {
      if (GameOperationsService.isUpgradeImprovementOperation(o)) {
        this.upgradeImprovement(o.improvementId, true);
      } else if (GameOperationsService.isUpgradeImprovementWithoutUnfreezeOperation(o)) {
        this.upgradeImprovement(o.improvementId);
      } else if (GameOperationsService.isRechargeEnergyOperation(o)) {
        this.applyRecharge();
      } else if (GameOperationsService.isSpendEnergyOperation(o)) {
        this.applySpendEnergy(o);
      }
    });

    const prev = new Date(this.state.actualAt).getTime();

    const deltaInSeconds = (new Date().getTime() - prev) / 1000;
    const gemsMined = this.getIncomePerSecond(this.state.totalIncomePerHour) * deltaInSeconds;

    this.state.gems = String(parseInt(this.state.gems) + gemsMined);
    const energy = this.getEnergyPerSecond(this.state.maxEnergy, this.state.energyRecoveryInMinutes) * deltaInSeconds;
    this.state.energy = Math.min(this.state.maxEnergy, this.state.energy + energy);

    this.state.actualAt = this.projectDate.date.toISOString();

    return this.state;
  }

  public addPoints(points: number): void {
    this.state.gems = String(parseInt(this.state.gems) + points);
  }

  private getIncomePerSecond(totalIncomePerHour: number): number {
    return totalIncomePerHour / DateUtils.hoursToSeconds(1);
  }

  private getEnergyPerSecond(maxEnergy: number, recoveryTimeInMinutes: number): number {
    return maxEnergy / DateUtils.daysToSeconds(recoveryTimeInMinutes / 60 / 24);
  }

  public addRecharges(count: number): void {
    this.state.energyRechargersCount += count;
  }

  public addMultiplier(value: number): void {
    this.state.energyMultiplier *= value;
  }

  private applySpendEnergy(operation: SpendEnergyOperation): void {
    if (this.state.energy - operation.energyToSpend < 0) return;

    this.state = {
      ...this.state,
      energy: this.state.energy - operation.energyToSpend,
      gems: String(parseInt(this.state.gems) + this.state.energyMultiplier * operation.energyToSpend),
    };
  }

  private applyRecharge(): void {
    if (this.state.energyRechargersCount === 0) return;

    const maxEnergy = this.state.maxEnergy;

    this.state = {
      ...this.state,
      energy: maxEnergy,
      maxEnergy,
      energyRechargersCount: this.state.energyRechargersCount - 1,
    };

    if (this.gameStatusMocks.getStatus() === GameStatusType.Mocks) {
      this.state.energyRechargersCount = Math.max(1, this.state.energyRechargersCount);
    }
  }

  private upgradeImprovement(improvementId: string, isUpgradeWithoutUnfreeze?: boolean): void {
    const improvementIndex = this.state.improvements.findIndex((i) => i.id === improvementId);

    if (improvementIndex === -1) return;

    const improvement = this.state.improvements[improvementIndex];

    const newCurrentLevel = improvement.currentLevel + 1;
    const newNextLevel = newCurrentLevel + 1;

    const baseIncomePerHour =
      improvement.currentLevel !== 0
        ? improvement.currentLevelIncomePerHour / improvement.currentLevel
        : improvement.nextLevelIncomePerHour;
    const newCurrentLevelIncomePerHour = baseIncomePerHour * newCurrentLevel;
    const newNextLevelIncomePerHour = baseIncomePerHour * newNextLevel;

    const baseCost =
      improvement.currentLevel !== 0 ? improvement.upgradeCost / improvement.currentLevel : improvement.upgradeCost;
    const upgradeCost = Math.round(baseCost * Math.pow(2, newCurrentLevel));
    const incomePerHourDifference = improvement.nextLevelIncomePerHour - improvement.currentLevelIncomePerHour;

    const isDoubleCost = isUpgradeWithoutUnfreeze || this.projectDate.getTimeBefore(improvement.frozenUntil) !== 0;
    const costForGems = isDoubleCost ? improvement.upgradeCost * 2 : improvement.upgradeCost;

    const now = this.projectDate.date;
    const dateToFrozenUntil = this.projectDate.date;
    if (newCurrentLevel === 1) {
      dateToFrozenUntil.setSeconds(now.getSeconds() + 10);
    } else if (newCurrentLevel === 2) {
      dateToFrozenUntil.setSeconds(now.getSeconds() + 30);
    } else if (newCurrentLevel === 3) {
      dateToFrozenUntil.setSeconds(now.getSeconds() + 90);
    } else if (newCurrentLevel === 4) {
      dateToFrozenUntil.setMinutes(now.getMinutes() + 5);
    } else if (newCurrentLevel === 5) {
      dateToFrozenUntil.setMinutes(now.getMinutes() + 30);
    } else if (newCurrentLevel === 6) {
      dateToFrozenUntil.setHours(now.getHours() + 1);
    } else {
      dateToFrozenUntil.setHours(now.getHours() + 2);
    }

    this.state = {
      ...this.state,
      gems: String(parseInt(this.state.gems) - costForGems),
      totalIncomePerHour: this.state.totalIncomePerHour + incomePerHourDifference,
    };

    this.state.improvements[improvementIndex] = {
      ...improvement,
      currentLevel: newCurrentLevel,
      currentLevelIncomePerHour: newCurrentLevelIncomePerHour,
      nextLevelIncomePerHour: newNextLevelIncomePerHour,
      upgradeCost,
      frozenUntil: dateToFrozenUntil.toISOString(),
    };
  }
}

injected(ClickerStateMocks, DI_TOKENS.projectDate, DI_TOKENS.gameStatusMocks);

// фиксированные моки для завершенного проекта
const MOCKS_STATUS_CLICKER_STATE: ClickerState = {
  gems: '0',
  totalIncomePerHour: 0,
  energy: 1000,
  maxEnergy: 1000,
  energyRechargersCount: 2,
  energyMultiplier: 1,
  energyRecoveryInMinutes: 180,
  improvements: improvementsList.slice(),
  actualAt: new Date().toISOString(),
};

const CLICKER_STATE_MOCKS: ClickerState = {
  gems: '10000',
  totalIncomePerHour: 3600,
  energy: 100,
  maxEnergy: 100,
  energyRechargersCount: 2,
  energyMultiplier: 1,
  energyRecoveryInMinutes: 180,
  improvements: improvementsList.slice(),
  actualAt: new Date().toISOString(),
};
